/**
 * Utilidad para manejo de almacenamiento de datos de autenticación
 * Maneja localStorage para persistir token y datos de usuario
 */

const STORAGE_KEYS = {
  TOKEN: 'helpmed_auth_token',
  USER: 'helpmed_user_data',
  REMEMBER_ME: 'helpmed_remember_me'
}

class AuthStorage {
  constructor() {
    this.isLocalStorageAvailable = this.checkLocalStorageAvailable()
  }

  /**
   * Verifica si localStorage está disponible
   */
  checkLocalStorageAvailable() {
    try {
      const testKey = 'test'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch (e) {
      console.warn('localStorage no está disponible')
      return false
    }
  }

  /**
   * Guarda el token de autenticación
   * @param {string} token - Token JWT
   */
  saveToken(token) {
    if (this.isLocalStorageAvailable && token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token)
    }
  }

  /**
   * Obtiene el token de autenticación
   * @returns {string|null} Token guardado o null
   */
  getToken() {
    if (this.isLocalStorageAvailable) {
      return localStorage.getItem(STORAGE_KEYS.TOKEN)
    }
    return null
  }

  /**
   * Guarda los datos del usuario
   * @param {Object} user - Datos del usuario
   */
  saveUser(user) {
    if (this.isLocalStorageAvailable && user) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
    }
  }

  /**
   * Obtiene los datos del usuario
   * @returns {Object|null} Datos del usuario o null
   */
  getUser() {
    if (this.isLocalStorageAvailable) {
      const userData = localStorage.getItem(STORAGE_KEYS.USER)
      if (userData) {
        try {
          return JSON.parse(userData)
        } catch (error) {
          console.error('Error parsing user data:', error)
          this.clearUser()
        }
      }
    }
    return null
  }

  /**
   * Guarda las credenciales si el usuario selecciona "Recordar"
   * @param {string} username - Nombre de usuario
   * @param {boolean} remember - Si debe recordar las credenciales
   */
  saveCredentials(username, remember = false) {
    if (this.isLocalStorageAvailable) {
      if (remember && username) {
        localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, username)
      } else {
        localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME)
      }
    }
  }

  /**
   * Obtiene las credenciales guardadas
   * @returns {string|null} Nombre de usuario guardado o null
   */
  getSavedEmail() {
    if (this.isLocalStorageAvailable) {
      return localStorage.getItem(STORAGE_KEYS.REMEMBER_ME)
    }
    return null
  }

  /**
   * Obtiene el nombre de usuario guardado (alias para getSavedEmail por compatibilidad)
   * @returns {string|null} Nombre de usuario guardado o null
   */
  getSavedUsername() {
    return this.getSavedEmail()
  }

  /**
   * Verifica si hay una sesión válida guardada
   * @returns {boolean} True si hay token y usuario guardados
   */
  hasValidSession() {
    const token = this.getToken()
    const user = this.getUser()
    
    if (!token || !user) {
      return false
    }

    // Verificar que el token no esté expirado
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      
      return payload.exp > currentTime
    } catch (error) {
      console.error('Error parsing token:', error)
      return false
    }
  }

  /**
   * Limpia el token de autenticación
   */
  clearToken() {
    if (this.isLocalStorageAvailable) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN)
    }
  }

  /**
   * Limpia los datos del usuario
   */
  clearUser() {
    if (this.isLocalStorageAvailable) {
      localStorage.removeItem(STORAGE_KEYS.USER)
    }
  }

  /**
   * Limpia todos los datos de autenticación
   */
  clearAll() {
    if (this.isLocalStorageAvailable) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN)
      localStorage.removeItem(STORAGE_KEYS.USER)
      // No limpiar REMEMBER_ME a menos que sea explícito
    }
  }

  /**
   * Limpia todo incluyendo credenciales recordadas
   */
  clearEverything() {
    if (this.isLocalStorageAvailable) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN)
      localStorage.removeItem(STORAGE_KEYS.USER)
      localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME)
    }
  }

  /**
   * Actualiza el último acceso del usuario
   */
  updateLastAccess() {
    const user = this.getUser()
    if (user) {
      user.ultimo_acceso = new Date().toISOString()
      this.saveUser(user)
    }
  }

  /**
   * Verifica si el token está próximo a expirar (menos de 5 minutos)
   * @returns {boolean} True si el token expira pronto
   */
  isTokenExpiringSoon() {
    const token = this.getToken()
    if (!token) return false

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      const timeToExpiry = payload.exp - currentTime
      
      // Retorna true si expira en menos de 5 minutos (300 segundos)
      return timeToExpiry < 300 && timeToExpiry > 0
    } catch (error) {
      console.error('Error checking token expiry:', error)
      return false
    }
  }

  /**
   * Obtiene información del token decodificado
   * @returns {Object|null} Payload del token o null
   */
  getTokenInfo() {
    const token = this.getToken()
    if (!token) return null

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return {
        id: payload.id,
        correo: payload.correo,
        nombre_usuario: payload.nombre_usuario,
        id_rol: payload.id_rol,
        iat: payload.iat,
        exp: payload.exp,
        expiresAt: new Date(payload.exp * 1000),
        issuedAt: new Date(payload.iat * 1000)
      }
    } catch (error) {
      console.error('Error parsing token info:', error)
      return null
    }
  }
}

// Exportar instancia singleton
export const authStorage = new AuthStorage()